import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dismissNotice, fetchNotices, type Notice } from '../api/notices'
import { useSession } from '../useSession'

/**
 * What the studio is telling this person today (D-790).
 *
 * <b>Above the navigation, like the two banners beside it</b> (D-386, D-520),
 * so it cannot be scrolled past or mistaken for part of a screen. It draws
 * nothing at all when there is nothing to say, which is every ordinary day.
 *
 * <b>Why this is not what's new.</b> That announces a feature and ships in the
 * build; this says <i>we know about this and here is what to do meanwhile</i>,
 * and has to be publishable while the defect is live — which is precisely when
 * a deploy is the thing you cannot afford (D-33).
 */
export default function NoticeBanner() {
  const { session } = useSession()
  const client = useQueryClient()

  const { data } = useQuery({
    queryKey: ['notices'],
    queryFn: fetchNotices,
    // Signed out there is nothing to ask for, and asking would 401 on the
    // sign-in page for everybody who is not signed in yet.
    enabled: Boolean(session),
    staleTime: 60_000,
  })

  const clear = useMutation({
    mutationFn: dismissNotice,
    onSuccess: () => client.invalidateQueries({ queryKey: ['notices'] }),
  })

  const notices = data ?? []
  if (notices.length === 0) return null

  return (
    <div>
      {notices.map((notice) => (
        <NoticeLine key={notice.id} notice={notice} onDismiss={() => clear.mutate(notice.id)} />
      ))}
    </div>
  )
}

function NoticeLine({ notice, onDismiss }: { notice: Notice; onDismiss: () => void }) {
  /*
   * <b>Amber for something wrong, neutral for something worth knowing.</b> The
   * tone is the studio's own word when it writes the notice, not something
   * derived here — a screen guessing how serious a sentence is would get it
   * wrong on exactly the sentence that mattered.
   */
  const issue = notice.tone === 'Issue'

  return (
    <div
      role="status"
      className={`flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5 px-4 py-1.5 text-center text-[12.5px] ${
        issue ? 'bg-warn-bg text-warn' : 'bg-surface-2 text-ink-2'
      }`}
    >
      <span className="font-semibold">{notice.headline}</span>
      <span className="font-normal opacity-90">{notice.body}</span>

      {/*
        <b>Only where the studio allowed one</b> (D-790). Most notices are read
        once and then noise; an outage genuinely should stay put, and that is
        the studio's call rather than this component's. The endpoint refuses it
        too — a control a screen merely omits is one a request can still reach.
      */}
      {notice.dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss: ${notice.headline}`}
          className="ml-1 rounded px-1.5 py-0.5 text-[13px] leading-none font-bold opacity-70 hover:opacity-100"
        >
          &times;
        </button>
      )}
    </div>
  )
}
